import React, { useEffect } from 'react';
import type { Place, RowBoundary } from '../types/warehouse.types';
import { getRowId, getPlaceById } from '../utils/placeUtils';

interface UseAutoRowProjectionProps {
  selectedPlaceIds: Set<string>;
  places: Map<string, Place>;
  rowBoundaries: RowBoundary[];
  setSelectedRow: (row: RowBoundary | null) => void;
}

/**
 * Hook for auto-opening row projection when selecting places from a single row
 * Automatically opens 2D projection if all selected places are from the same row
 */
export function useAutoRowProjection({ selectedPlaceIds, places, rowBoundaries, setSelectedRow }: UseAutoRowProjectionProps): void {
  useEffect(() => {
    if (selectedPlaceIds.size > 0) {
      // Find which rows the selected places belong to
      const selectedRowIds = new Set<string>();
      
      // Iterate through all places to find matching place IDs
      selectedPlaceIds.forEach(placeId => {
        const place = getPlaceById(places, placeId);
        if (place) {
          const rowId = getRowId(place);
          selectedRowIds.add(rowId);
        }
      });
      
      // If all selected places are from the same row, open projection
      if (selectedRowIds.size === 1) {
        const rowId = Array.from(selectedRowIds)[0];
        // Find row boundary with exact match (not just includes)
        const rowBoundary = rowBoundaries.find(r => r.name === rowId);
        if (rowBoundary) {
          setSelectedRow(rowBoundary);
        }
      } else if (selectedRowIds.size > 1) {
        // Multiple rows selected, close projection
        setSelectedRow(null);
      }
    } else {
      // No selection, close projection
      setSelectedRow(null);
    }
  }, [selectedPlaceIds, places, rowBoundaries, setSelectedRow]);
}
